/**
 * CreateTeamModal — premium dark-esports flow for spinning up a brand-new
 * standalone team (no league required). The creator becomes the permanent
 * Owner; they pick a name, optional banner, hard roster cap, supported
 * games, and an optional pitch. Once submitted, the team lands in the
 * caller's onCreated callback (the dashboard / discovery page open the
 * Team HQ on it immediately).
 *
 * Visual rules — strict Cinematic Cyberpunk:
 *   - deep navy-black bg, single muted steel-blue (#14b8a6) accent
 *   - Press Start 2P headers, Inter body
 *   - no emoji, no neon glow, no rainbow chips
 *   - left-aligned, generous spacing, glassmorphism
 */
import React, { useMemo, useState } from "react";
import { createStandaloneTeam, fetchGames } from "../lib/api";
import { useEffect } from "react";

const COMPETITIVE_LEVELS = [
  { id: "casual",      label: "CASUAL",      caption: "For-fun scrims, no pressure." },
  { id: "competitive", label: "COMPETITIVE", caption: "Ranked grinds + scheduled scrims." },
  { id: "pro",         label: "PRO",         caption: "Tier-1 esports org workflow." },
];

const REGIONS = ["NA", "EU", "LATAM", "APAC", "OCE", "BR", "ME", "AF", "GLOBAL"];

export default function CreateTeamModal({ open, onClose, onCreated, onToast }) {
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);          // 1: identity → 2: capacity + games → 3: confirm
  const [form, setForm] = useState({
    name: "",
    description: "",
    banner_url: "",
    pfp_url: "",
    max_members: 6,
    supported_games: [],
    competitive_level: "competitive",
    region: "NA",
    social_link: "",
  });

  // Load the master game list once so the chip-picker is real, not a
  // freeform text field. Errors degrade silently → a free-form input is
  // shown as a fallback so the modal never blocks.
  useEffect(() => {
    if (!open) return;
    fetchGames().then(setGames).catch(() => setGames([]));
  }, [open]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const toggleGame = (g) =>
    setForm((f) => ({
      ...f,
      supported_games: f.supported_games.includes(g)
        ? f.supported_games.filter((x) => x !== g)
        : [...f.supported_games, g].slice(0, 6),
    }));

  const trimmedName = form.name.trim();
  const canStep2 = trimmedName.length >= 3 && trimmedName.length <= 60;
  const canStep3 =
    canStep2 &&
    form.supported_games.length > 0 &&
    form.max_members >= 1 &&
    form.max_members <= 50;

  const submit = async () => {
    if (!canStep3) return;
    setBusy(true);
    try {
      const team = await createStandaloneTeam({
        name:              trimmedName,
        description:       form.description.trim() || null,
        banner_url:        form.banner_url.trim() || null,
        pfp_url:           form.pfp_url.trim() || null,
        max_members:       Number(form.max_members),
        supported_games:   form.supported_games,
        competitive_level: form.competitive_level,
        region:            form.region,
        social_link:       form.social_link.trim() || null,
      });
      onToast?.(`${team.name} is live. You're the OWNER.`);
      onCreated?.(team);
      // Reset for next open
      setStep(1);
      setForm({
        name: "", description: "", banner_url: "", pfp_url: "",
        max_members: 6, supported_games: [],
        competitive_level: "competitive", region: "NA", social_link: "",
      });
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't create team.");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="create-team-modal"
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-2 sm:p-6 bg-black/70 backdrop-blur-md animate-fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden border border-[rgba(20,184,166,0.35)] bg-[#111113]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner band — single muted steel-blue, NOT a gradient soup */}
        <div className="relative h-24 sm:h-28 bg-gradient-to-b from-[#14b8a6]/15 to-transparent border-b border-[rgba(20,184,166,0.18)] flex items-center justify-between px-5">
          <div>
            <p
              className="text-[9px] tracking-[5px] text-[#14b8a6] font-bold mb-1"
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              FOUND YOUR TEAM
            </p>
            <p
              className="text-white text-lg sm:text-xl font-bold leading-tight"
              style={{ fontFamily: '"Press Start 2P", monospace', letterSpacing: 1 }}
            >
              STEP {step} / 3
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="create-team-close"
            className="w-9 h-9 rounded-full bg-black/50 border border-white/10 text-white/70 hover:text-white hover:border-[#14b8a6]/50 transition-colors"
            aria-label="Close"
          >
            <i className="fas fa-xmark" />
          </button>
        </div>

        {/* Stepper rail */}
        <div className="px-5 pt-4">
          <div className="h-1 bg-black/40 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#14b8a6] transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
              data-testid="create-team-progress"
            />
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <section data-testid="create-team-step-1" className="space-y-5">
              <div>
                <Label tag="01" text="TEAM NAME" />
                <input
                  autoFocus
                  value={form.name}
                  onChange={set("name")}
                  maxLength={60}
                  placeholder="Pulse Phantasms"
                  data-testid="create-team-name"
                  className="w-full bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-lg placeholder-white/30 transition-colors"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  3–60 characters. You'll be the permanent OWNER — only
                  YOU can disband it. {trimmedName.length}/60
                </p>
              </div>

              <div>
                <Label tag="02" text="DESCRIPTION · OPTIONAL" />
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={set("description")}
                  maxLength={300}
                  placeholder="Tryout-ready esports org. Diamond+ only. Scrim Tue/Thu."
                  data-testid="create-team-description"
                  className="w-full bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 resize-none transition-colors"
                />
                <p className="text-[10px] text-white/35 mt-1">
                  Shown on your Team Hub + HQ Discovery card. {form.description.length}/300
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label tag="03" text="REGION" />
                  <select
                    value={form.region}
                    onChange={set("region")}
                    data-testid="create-team-region"
                    className="w-full bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-sm transition-colors"
                  >
                    {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label tag="04" text="SOCIAL · OPTIONAL" />
                  <input
                    value={form.social_link}
                    onChange={set("social_link")}
                    placeholder="discord.gg / twitter.com / …"
                    data-testid="create-team-social"
                    className="w-full bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 transition-colors"
                  />
                </div>
              </div>
            </section>
          )}

          {step === 2 && (
            <section data-testid="create-team-step-2" className="space-y-5">
              <div>
                <Label tag="05" text="GAMES YOU PLAY" />
                <p className="text-[11px] text-white/55 mb-3">
                  Pick the titles your team competes in. You'll build a
                  separate roster per game/split from inside the Team Hub
                  (e.g. one Valorant 5-stack + one LoL 5-stack).
                  <span className="text-[#14b8a6]">{form.supported_games.length}/6</span>
                </p>
                {games.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {games.map((g) => {
                      const active = form.supported_games.includes(g.name);
                      return (
                        <button
                          key={g.name}
                          type="button"
                          onClick={() => toggleGame(g.name)}
                          data-testid={`create-team-game-${g.name}`}
                          className={`text-[11px] tracking-wider px-3 py-2 rounded-lg border transition-colors ${
                            active
                              ? "bg-[#14b8a6]/20 border-[#14b8a6]/60 text-[#14b8a6]"
                              : "bg-black/40 border-white/10 text-white/70 hover:border-[#14b8a6]/40 hover:text-white"
                          }`}
                        >
                          {g.icon && <i className={`fas ${g.icon} mr-1.5 opacity-80`} />}
                          {g.name.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    value={form.supported_games.join(", ")}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, supported_games: e.target.value.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 6) }))
                    }
                    placeholder="Valorant, League of Legends"
                    data-testid="create-team-games-fallback"
                    className="w-full bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-sm transition-colors"
                  />
                )}
              </div>

              <div>
                <Label tag="06" text="ROSTER CAP" />
                <p className="text-[11px] text-white/55 mb-3">
                  Maximum number of players (including subs). You can change
                  it later from Team Settings. Cap is enforced on every join
                  request — no surprise over-capacity rosters.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={form.max_members}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, max_members: Math.max(1, Math.min(50, Number(e.target.value) || 1)) }))
                    }
                    data-testid="create-team-cap"
                    className="w-24 bg-black/40 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/70 focus:outline-none rounded-xl px-4 py-3 text-white text-lg font-bold text-center transition-colors"
                  />
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={Math.min(20, form.max_members)}
                    onChange={(e) => setForm((f) => ({ ...f, max_members: Number(e.target.value) }))}
                    className="flex-1 accent-[#14b8a6]"
                  />
                  <span className="text-[10px] tracking-widest text-white/40 w-16 text-right">PLAYERS</span>
                </div>
              </div>

              <div>
                <Label tag="07" text="COMPETITIVE LEVEL" />
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {COMPETITIVE_LEVELS.map((lv) => {
                    const active = form.competitive_level === lv.id;
                    return (
                      <button
                        type="button"
                        key={lv.id}
                        onClick={() => setForm((f) => ({ ...f, competitive_level: lv.id }))}
                        data-testid={`create-team-level-${lv.id}`}
                        className={`text-left rounded-xl border px-3 py-3 transition-colors ${
                          active
                            ? "bg-[#14b8a6]/15 border-[#14b8a6]/60"
                            : "bg-black/40 border-white/10 hover:border-[#14b8a6]/30"
                        }`}
                      >
                        <p className={`text-[11px] font-bold tracking-widest ${active ? "text-[#14b8a6]" : "text-white"}`}>
                          {lv.label}
                        </p>
                        <p className="text-[10px] text-white/55 mt-1 leading-snug">{lv.caption}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {step === 3 && (
            <section data-testid="create-team-step-3" className="space-y-4">
              <div className="rounded-2xl border border-[rgba(20,184,166,0.35)] bg-black/40 p-4">
                <p className="text-[9px] tracking-[4px] text-[#14b8a6] font-bold mb-3">
                  CONFIRM YOUR TEAM
                </p>
                <Row k="NAME"             v={trimmedName} />
                <Row k="REGION"           v={form.region} />
                <Row k="ROSTER CAP"       v={`${form.max_members} players`} />
                <Row k="LEVEL"            v={form.competitive_level.toUpperCase()} />
                <Row k="GAMES"            v={form.supported_games.join(" · ")} />
                {form.description && <Row k="PITCH" v={form.description} mono />}
                {form.social_link && <Row k="SOCIAL" v={form.social_link} mono />}
              </div>
              <p className="text-[11px] text-white/55 leading-snug">
                You'll be the permanent <span className="text-[#14b8a6]">OWNER</span>{" "}
                with a crown badge. You can build per-game rosters from
                the Team Hub once it's live. <b className="text-white/85">You don't have
                to be on any roster yourself</b> — manage from the sidelines.
              </p>
            </section>
          )}
        </div>

        {/* Footer / step controls */}
        <div className="border-t border-white/10 bg-black/40 px-5 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : onClose?.())}
            disabled={busy}
            data-testid="create-team-back"
            className="text-[10px] tracking-widest text-white/55 hover:text-white px-3 py-2 rounded-lg disabled:opacity-50"
          >
            <i className="fas fa-arrow-left mr-2 opacity-70" />
            {step > 1 ? "BACK" : "CANCEL"}
          </button>
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canStep2) || (step === 2 && form.supported_games.length === 0)}
              data-testid="create-team-next"
              className="text-[11px] tracking-widest font-bold px-5 py-2.5 rounded-xl bg-[#14b8a6] text-black disabled:opacity-40 hover:bg-[#7ab1ff] transition-colors"
            >
              CONTINUE
              <i className="fas fa-arrow-right ml-2" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy || !canStep3}
              data-testid="create-team-submit"
              className="text-[11px] tracking-widest font-bold px-5 py-2.5 rounded-xl bg-[#14b8a6] text-black disabled:opacity-40 hover:bg-[#7ab1ff] transition-colors"
            >
              {busy ? "CREATING…" : "LOCK IN THE TEAM"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Label = ({ tag, text }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="text-[9px] font-mono text-[#14b8a6]/60">{tag}</span>
    <span className="text-[10px] tracking-[3px] text-white/80 font-semibold">{text}</span>
  </div>
);

const Row = ({ k, v, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
    <span className="text-[10px] tracking-widest text-white/45 shrink-0">{k}</span>
    <span className={`text-[12px] text-right max-w-[68%] ${mono ? "font-mono text-white/85" : "text-white"}`}>
      {v || "—"}
    </span>
  </div>
);
